import React, { useCallback, useEffect, useRef } from 'react';
import styled from '@xstyled/styled-components';
import { CloseButton } from '~/ui/buttons/CloseButton';
import { SlideOut } from '~/ui/slide-out/SlideOut';
import { TrainingState, useTrainingStore } from '../state';
import { Header } from './Header';
import { OpenNavigationButton, COURSE_NAVIGATION_DIALOG_ID } from './NavButton';
import { TableOfContent } from './TableOfContent';
import { TrainingTitle } from './TrainingTitle';
import { select } from '~/lib/select';
import { useOpenCloseAudioEffect } from '~/ui/audio-effect/useOpenCloseAudioEffect';
import { useLocation } from 'react-router-dom';
import { useQueryParams } from '~/lib/useQuery';
import { useFocusTrap } from '~/lib/a11y/useFocusTrap';
import { t } from 'ttag';

const Panel = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
`;

const SlideOutContent = styled.divBox`
  display: grid;
  width: 100%;
  height: 100%;
  min-height: 0;
  /* minmax(0, 1fr): allow TOC row to shrink so overflow:auto can scroll
     (default min-height:auto blocks scroll on short / 400% zoom viewports). */
  grid-template-rows: auto auto minmax(0, 1fr);
  box-sizing: border-box;
  overflow: hidden;

  ol {
    li {
      ul {
        margin-right: -3px;
        li {
          a {
            padding-right: 10px;
            gap: 8px;
            margin-left: 18px;
          }
        }
      }
    }
  }
`;

const TocScroll = styled.div`
  min-height: 0;
  height: 100%;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;

SlideOutContent.defaultProps = {
  w: { _: '100vw', sm: '290px' }
};

const selector = (s: TrainingState) => select(s, 'open', 'toggleNav');

export const Navigation = () => {
  const queryParams = useQueryParams();
  const { open, toggleNav } = useTrainingStore(selector);
  const panelRef = useRef<HTMLDivElement>(null);

  const hideHomeIcon = queryParams.get('hideHomeIcon') === 'true';

  useOpenCloseAudioEffect(open);
  useFocusTrap(panelRef, open);

  const location = useLocation();
  const params = new URLSearchParams(location.search);

  useEffect(() => {
    if (params.get('menu') === 'true') {
      toggleNav(true);
    }
  }, []);

  const close = useCallback(() => {
    toggleNav(false);
  }, [toggleNav]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  return (
    <>
      {queryParams.get('removeAllButtons') !== 'true' && (
        <OpenNavigationButton />
      )}
      <SlideOut open={open} side="left" overlayClicked={close}>
        <Panel
          ref={panelRef}
          id={COURSE_NAVIGATION_DIALOG_ID}
          role="dialog"
          aria-modal="true"
          aria-label={t`Course navigation`}
        >
          <CloseButton autoFocus onClick={close} />
          <SlideOutContent className="navigationStyleForDarkTheme">
            <Header hideHomeIcon={hideHomeIcon} />
            <TrainingTitle />
            <TocScroll>
              <TableOfContent />
            </TocScroll>
          </SlideOutContent>
        </Panel>
      </SlideOut>
    </>
  );
};
